import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { selectTxnId } from "../mapSelectors";
import { useMapStore } from "../mapStore";
import type { MapState } from "../mapTypes";

const channelName = (channel: string) => `eva-map-txnid:${channel}`;

/** Main tab → popout: a transaction to mirror. */
interface TxnIdMessage {
  type: "txnId";
  txnId: MapState["txnId"];
}
/** Popout → main tab: "send me the current transaction". */
interface RequestMessage {
  type: "request";
}

// BroadcastChannel is same-origin only, but any same-origin sender (a tab on
// another deploy, a same-origin XSS) shares the channel, and what arrives goes
// straight into global state and a react-query key. Accept only a transaction
// the legend can consume: useChartLegendItems destructures `legendItems` off
// `legend` / `facilityLegend` unguarded, so a missing array there would throw
// in the popout.
const isTxnIdMessage = (data: unknown): data is TxnIdMessage => {
  if (typeof data !== "object" || data === null) return false;
  const { type, txnId } = data as Partial<TxnIdMessage>;
  return (
    type === "txnId" &&
    typeof txnId?.id === "string" &&
    Array.isArray(txnId.legend?.legendItems) &&
    Array.isArray(txnId.facilityLegend?.legendItems)
  );
};

// Rejected messages are dropped, so the rogue-sender case above would otherwise
// leave no trace. A breadcrumb keeps it visible in monitoring without throwing.
const noteRejectedMessage = (data: unknown): void => {
  Sentry.addBreadcrumb({
    message: "useTxnIdCrossTabSubscriber: rejected malformed txnId message",
    category: "map_txnid_bridge",
    level: "warning",
    data: { dataType: typeof data, type: (data as { type?: unknown })?.type }
  });
};

const isRequestMessage = (data: unknown): data is RequestMessage =>
  typeof data === "object" &&
  data !== null &&
  (data as Partial<RequestMessage>).type === "request";

/**
 * Live cross-tab sync for the map transaction (`txnId`), EVA-124.
 *
 * The chart legend builds its items, counts and colours from `txnId.legend`,
 * which only the map hooks write. Until the State Management migration (#5871)
 * the popout chart tab received every `map/setTxnId` through redux-state-sync;
 * as a Zustand field it no longer travels, so a popout's legend froze at the
 * snapshot taken when it was opened while its chart kept re-querying through
 * the still-synced Redux filter.
 *
 * Opt-in and explicit, as the state ownership contract (§11.3) asks: the main
 * tab publishes each `txnId` change on a BroadcastChannel keyed by the tab
 * channel id (the same id the popout URL carries), and the popout mirrors it
 * into its own store. The opening snapshot in usePopoutInitialState still
 * covers the initial value; this covers everything after.
 *
 * BroadcastChannel does not buffer, so a transaction published while the popout
 * is still booting (the map request is debounced, so "change group-by, click
 * Pop out" snapshots the previous one) would be lost. The subscriber therefore
 * asks for the current value once it attaches, and the publisher answers.
 *
 * Mount the publisher in the main app only. Even if a popout ever mounted it,
 * the store's setTxnId change guard (same ids → no write → no notification)
 * ends any echo after one hop.
 *
 * That same guard bounds what this bridge can deliver: a transaction whose
 * `legend` changed under unchanged `id`/`facilityId` is neither published nor
 * applied. In practice the backend mints a fresh id for every map request
 * (see api/map.ts), and palette, group-by and filter changes all issue one, so
 * the ceiling is the main tab's own.
 */
export function useTxnIdCrossTabPublisher(): void {
  const channel = useSelector((state: RootState) => state.channel.channel);

  useEffect(() => {
    if (!channel) return;
    const bc = new BroadcastChannel(channelName(channel));
    const publish = (txnId: MapState["txnId"]) => {
      const message: TxnIdMessage = { type: "txnId", txnId };
      bc.postMessage(message);
    };
    const unsubscribe = useMapStore.subscribe(selectTxnId, publish);
    bc.onmessage = (event: MessageEvent<unknown>) => {
      if (isRequestMessage(event.data)) publish(selectTxnId(useMapStore.getState()));
    };
    return () => {
      unsubscribe();
      bc.close();
    };
  }, [channel]);
}

/** Mount in the popout chart tab; the popout is a mirror, not a source. */
export function useTxnIdCrossTabSubscriber(): void {
  const channel = useSelector((state: RootState) => state.channel.channel);

  useEffect(() => {
    if (!channel) return;
    const bc = new BroadcastChannel(channelName(channel));
    bc.onmessage = (event: MessageEvent<unknown>) => {
      if (isTxnIdMessage(event.data)) {
        useMapStore.getState().setTxnId(event.data.txnId);
      } else {
        noteRejectedMessage(event.data);
      }
    };
    const request: RequestMessage = { type: "request" };
    bc.postMessage(request);
    return () => bc.close();
  }, [channel]);
}
